import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/useToast";
import { eventsApi } from "@/lib/humhub-api/events";
import type { RecurrenceScope, ReminderData } from "@/lib/humhub-api/types";

interface UseCreateEventRemindersOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

interface UseCreateEventRemindersReturn {
  isLoading: boolean;
  error: Error | null;
  createReminders: (
    eventId: number,
    reminders: ReminderData[],
    scope?: RecurrenceScope,
  ) => Promise<void>;
}

export const useCreateEventReminders = (
  options?: UseCreateEventRemindersOptions,
): UseCreateEventRemindersReturn => {
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async ({
      eventId,
      reminders,
      scope,
    }: {
      eventId: number;
      reminders: ReminderData[];
      scope?: RecurrenceScope;
    }) => {
      return eventsApi.createEventReminders(eventId, reminders, scope);
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error) => {
      console.error("Error creating reminders:", error);
      options?.onError?.();
      toast({
        title: t("common.error"),
        description: t("eventManage.reminderCreationFailed"),
        variant: "error",
      });
    },
  });

  const createReminders = useCallback(
    async (
      eventId: number,
      reminders: ReminderData[],
      scope?: RecurrenceScope,
    ): Promise<void> => {
      return mutation.mutateAsync({ eventId, reminders, scope });
    },
    [mutation],
  );

  return {
    isLoading: mutation.isPending,
    error: mutation.error,
    createReminders,
  };
};
